import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { ApolloError, FetchResult, TypedDocumentNode } from '@apollo/client';
import { useGraphQLMutation } from '@betterment/js-runtime';
import { Banner, Box, BoxProps } from '@betterment/samba-react';
import { captureException } from '@sentry/browser';
import { Formik, FormikHelpers, FormikValues } from 'formik';
import { Kind } from 'graphql';
import invariant from 'tiny-invariant';

import { DisplayableError } from '~/graphql/schema';

export type ErrorMap<TFields extends FormikValues> = Record<
  string,
  keyof TFields | ((error: DisplayableError, formikHelpers: FormikHelpers<TFields>) => Promise<void> | void)
>;

export type Props<TFields extends FormikValues, TData extends WithDisplayableErrors, TVariables> = Omit<
  Parameters<typeof Formik<TFields>>[0],
  'onSubmit'
> & {
  mutation: TypedDocumentNode<TData, TVariables>;
  prepareValues: (values: TFields) => TVariables;
  errorHandlers: ErrorMap<TFields> | ((values: TFields) => ErrorMap<TFields>);
  onSuccess: (result: FetchResult<TData>) => void;
};

type DisplayableErrorCollection = {
  displayableErrors: Array<DisplayableError>;
};

export type WithDisplayableErrors = {
  [key: string]: 'Mutation' | DisplayableErrorCollection;
};

interface MutationFormContextProps {
  failureTreatment: 'default' | 'custom';
  shouldShowFailure: boolean;
  setFailureTreatment: (treatment: 'default' | 'custom') => void;
}

const MutationFormContext = createContext<MutationFormContextProps>({
  failureTreatment: 'default',
  shouldShowFailure: false,
  setFailureTreatment: _treatment => {},
});

function captureError(error: Error) {
  const apolloError = error.name === 'ApolloError' ? (error as ApolloError) : undefined;

  if (apolloError?.networkError) return;

  captureException(error);
}

// This should fairly reliably extract the field name we need to access the displayable errors for
// a given mutation result
function extractMutationFieldName<TData, TVariables>(mutation: TypedDocumentNode<TData, TVariables>) {
  const operationDefinition = mutation.definitions.find(defintion => defintion.kind === Kind.OPERATION_DEFINITION);
  const field = operationDefinition!.selectionSet.selections.find(selection => selection.kind === Kind.FIELD);
  return field!.name.value;
}

async function handleError<TFields extends FormikValues>(
  error: DisplayableError,
  errorMap: ErrorMap<TFields>,
  formikHelpers: FormikHelpers<TFields>
): Promise<{ handled: boolean; error: DisplayableError }> {
  const pathKey = error.path.join('/');
  const handler = errorMap[pathKey];

  if (typeof handler === 'string') {
    await formikHelpers.setFieldTouched(handler);
    formikHelpers.setFieldError(handler, error.message);
    return { handled: true, error };
  }

  if (typeof handler === 'function') {
    await handler(error, formikHelpers);
    return { handled: true, error };
  }

  return { handled: false, error };
}

/**
 * A component to abstract common graphql form submission concerns and ensure consistent error and failure handling. It models the
 * problem of form submission by considering 3 different states for a mutation submission
     - `success` - The mutation succeeded with no errors
     - `error` - The mutation has validation errors that the user is responsible for and requires no engineer involvement
     - `failure` - The mutation has failed to submit at all, and the problem needs to be investigated by an engineer    
 * Uses formik under the hood. To use you must provide the following props
 *    - mutation - The mutation document you'd use with `useGraphQLMutation`
 *    - prepareValues - A function that maps form values to mutation varaibles
 *    - errorHandlers - A Record with string keys corresponding to the graphql displayable error paths returned by the mutation. For nested
 *      paths use slashes for seperators (ie `user/lastName`). Values are either formik field names if you want the error to be displayed
        as part of the default field error message UI, or functions if you want custom handling. If the server returns any displayableErrors with paths
        that are not part of your errorHandlers, the form will treat this as a failure state
      - onSuccess - The function to invoke if the mutation succeeds
   You can also pass any formik props to the component and they will be forwarded along     
 */
export function GraphQLMutationForm<TFields extends FormikValues, TData extends WithDisplayableErrors, TVariables>({
  children,
  mutation,
  prepareValues,
  errorHandlers,
  onSuccess,
  ...props
}: Props<TFields, TData, TVariables>) {
  const [mutate] = useGraphQLMutation(mutation);

  const [shouldShowFailure, setShowFailure] = useState<boolean>(false);
  const [failureTreatment, setFailureTreatment] = useState<'custom' | 'default'>('default');

  const shouldUseDefaultFailureTreatment = failureTreatment === 'default';

  const submit = async (values: TFields, formikHelpers: FormikHelpers<TFields>) => {
    try {
      const mutationValues = prepareValues(values);
      const result = await mutate({ variables: mutationValues });
      const errorMap = typeof errorHandlers === 'function' ? errorHandlers(values) : errorHandlers;
      const resultFieldName = extractMutationFieldName(mutation);
      const { data } = result;
      if (data && typeof data[resultFieldName] === 'object' && data[resultFieldName].displayableErrors.length > 0) {
        setShowFailure(false);
        const trackedErrors = await Promise.all(
          data[resultFieldName].displayableErrors.map(error => handleError(error, errorMap, formikHelpers))
        );
        const unhandledErrors = trackedErrors.filter(error => !error.handled);

        invariant(
          unhandledErrors.length === 0,
          `The following displayableErrors were not handled ${unhandledErrors.map(error => error.error.path.join('/')).join(', ')}`
        );
      } else if (data && typeof data[resultFieldName] === 'object') {
        setShowFailure(false);
        onSuccess(result);
      } else if (result.errors) {
        throw new ApolloError({ graphQLErrors: result.errors });
      } else {
        invariant(false, 'unreachable');
      }
    } catch (error) {
      captureError(error as Error);
      setShowFailure(true);
    }
  };

  const context = useMemo(
    () => ({ shouldShowFailure, failureTreatment, setFailureTreatment }),
    [failureTreatment, shouldShowFailure]
  );

  return (
    <>
      {shouldUseDefaultFailureTreatment && (
        <Box role="alert">{shouldShowFailure && <Banner variant="critical">Something went wrong</Banner>}</Box>
      )}
      <MutationFormContext.Provider value={context}>
        <Formik<TFields> onSubmit={submit} {...props}>
          {children}
        </Formik>
      </MutationFormContext.Provider>
    </>
  );
}

// eslint-disable-next-line react/function-component-definition
GraphQLMutationForm.Failure = ({ children, css: cssProp }: BoxProps) => {
  const { setFailureTreatment, failureTreatment, shouldShowFailure } = useContext(MutationFormContext);

  useEffect(() => {
    setFailureTreatment('custom');
  }, [setFailureTreatment]);

  return (
    failureTreatment === 'custom' && (
      <Box role="alert" css={cssProp}>
        {shouldShowFailure && children}
      </Box>
    )
  );
};
